import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import SubServiceGrid from '../../components/seo/SubServiceGrid';
import { getGallery } from '../../seo/galleryConfig';

const handymanSubServices = [
  { label: 'Flat Pack Assembly', blurb: 'Wardrobes, beds, desks, kitchen units assembled and levelled.' },
  { label: 'Wall Mounting', blurb: 'TVs, mirrors, pictures — securely fitted with proper anchors.' },
  { label: 'Door Repairs', blurb: 'Sticking doors, dropped hinges, latch and handle replacements.' },
  { label: 'Lock Replacements', blurb: 'Yale, mortice, sash and uPVC multi-point locks — supplied & fitted.' },
  { label: 'Silicone & Sealant Works', blurb: 'Bath, basin, shower and kitchen worktop resealing — neat, mould-resistant.' },
  { label: 'Minor Carpentry', blurb: 'Shelving, skirting, architrave, simple cabinet repairs.' },
  { label: 'Shelving Installation', blurb: 'Fixed and floating shelves — properly anchored, perfectly level.' },
  { label: 'General Property Repairs', blurb: 'The mixed bag of small jobs every property collects.' },
  { label: 'Furniture Repairs', blurb: 'Wobbly chairs, broken drawer runners, loose handles, frame fixes.' },
  { label: 'Small Odd Jobs', blurb: 'Curtain poles, blinds, brackets, light fittings — fast turnaround.' },
  { label: 'Property Maintenance', href: '/services/property-maintenance', blurb: 'Planned care for landlords, agents and homeowners.' },
];

const faqs = [
  {
    q: 'What kind of jobs do your handymen do?',
    a: 'A genuine handyman is a generalist — and so are ours. Typical jobs include flat-pack assembly, picture and TV wall-mounting, door adjustments and lock changes, minor carpentry, silicone resealing in kitchens and bathrooms, painting touch-ups, curtain-pole and blind fitting, and small repair jobs across the property. If it does not need a specialist trade certificate, we can usually help.'
  },
  {
    q: 'Is there a minimum charge or call-out fee?',
    a: 'We work on either an hourly basis (with a minimum of one hour) or a fixed quote for larger jobs. There is no separate callout fee on top — the price you are quoted is the price you pay. For very small jobs we often recommend bundling several together to make the visit better value.'
  },
  {
    q: 'Do I need to provide the materials?',
    a: 'For most jobs you supply the items being fitted (the picture, the flat-pack furniture, the TV bracket). We bring our own tools, fixings, sealants and consumables. If you need us to source materials we are happy to do so and itemise them on the invoice.'
  },
  {
    q: 'Can you do multiple small jobs in one visit?',
    a: 'Yes — and we actively encourage it. Hand us a list when we arrive (or email it beforehand) and we will work through it efficiently. Booking three or four small tasks in a single visit is usually much cheaper than booking three or four separate visits.'
  },
  {
    q: 'Are your handymen insured?',
    a: 'Yes. All H.A.R.P Solutions engineers, including those primarily doing handyman work, carry full public liability insurance. If anything goes wrong on the job we are fully accountable.'
  },
  {
    q: 'Do you work for landlords and letting agents?',
    a: 'Frequently. We have a number of landlords and lettings agents who use us for end-of-tenancy fix-ups, mid-tenancy repairs and inventory snag lists. We can invoice the landlord directly, attend at agreed times with tenant notice, and provide written reports for your records.'
  },
];

const relatedServices = [
  { slug: 'property-maintenance', label: 'Property Maintenance', blurb: 'Ongoing care for landlords, agents and homeowners.' },
  { slug: 'plumbing', label: 'Plumbing Services', blurb: 'Need a plumber as well? Bundle the visit for better value.' },
  { slug: 'emergency-plumber', label: 'Emergency Plumber', blurb: '24/7 callouts for urgent water and heating problems.' },
];

const HandymanPage = () => (
  <ServicePageLayout
    seoTitle="Handyman Services Near Me | Local Handyman | H.A.R.P Solutions LTD"
    seoDescription="Reliable local handyman in Kent, London and Essex. Flat-pack assembly, wall-mounting, door repairs, silicone resealing, minor carpentry and general property repairs. Fully insured."
    canonicalPath="/services/handyman"
    keywords="handyman near me, local handyman, general repairs, flat pack assembly, wall mounting, door repairs, lock replacement, handyman Kent, handyman London, handyman Essex"
    pageTitle="Trusted Local Handyman for Kent, London & Essex"
    serviceName="Handyman Services"
    serviceSlug="handyman"
    galleryImages={getGallery('handyman')}
    heroSubheading="The little jobs that never get done — finally done properly."
    intro="Most properties accumulate a list of small jobs that everyone keeps putting off. Wobbly doors. Pictures still leaning against the wall six months after moving in. A flat-pack wardrobe in the corner that nobody fancies tackling. Our handyman service exists exactly for that list. Book one visit, hand us the list, and watch it disappear."
    bullets={[
      'One visit, lots of jobs — better value than separate calls',
      'Hourly rate or fixed quote — your choice',
      'Clean, polite, on-time — every visit',
      'Landlord and lettings agent-friendly',
      'Fully insured engineers',
      'No job too small — even single-bracket jobs welcome',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
  >
    <h2>What Is A "Handyman" — And Why It Matters</h2>
    <p>
      In the UK trades industry, "handyman" is often used loosely. Some firms send untrained workers and charge premium rates; others
      restrict themselves to so few jobs that you end up calling three different companies anyway. We take a different approach. A H.A.R.P
      Solutions handyman is a skilled multi-trade engineer — someone who is genuinely capable across plumbing, carpentry, painting, fixings,
      and minor electrical accessories. That breadth means a single visit can clear a long list of small jobs, which saves you time and
      money compared with booking specialists for each task.
    </p>
    <p>
      We serve homeowners, tenants, landlords, lettings agents, small businesses, café and shop owners across Kent, Greater London and
      Essex. Whether you need a single picture hung or your entire spare room kitted out with shelves, blinds and a desk, you get the same
      engineer-led service: tidy, on-time and properly insured.
    </p>

    <h2>Common Jobs We Handle</h2>
    <p>Here are the kinds of tasks customers regularly book us for. Click any sub-service for more, or call us if yours isn't listed — it's almost certainly something we can do.</p>

    <SubServiceGrid
      title="Handyman Sub-Services"
      subtitle="Click any tile with an arrow for the dedicated page. Items without arrows are covered by our wider handyman service — call us for a quote."
      items={handymanSubServices}
    />

    <h2 className="!mt-12">How A Typical Booking Works</h2>
    <ol>
      <li><strong>Send us your list.</strong> Either over the phone, via the contact form, or by WhatsApp. Even rough descriptions are fine.</li>
      <li><strong>We estimate the time.</strong> A skilled handyman gets a lot done per hour. We'll tell you honestly whether your list is a 2-hour job or a half-day.</li>
      <li><strong>Pick a slot.</strong> We give you a tight arrival window — no half-day waits.</li>
      <li><strong>The visit.</strong> We tick through your list. If we finish early, we'll ask if anything else needs doing rather than just leaving.</li>
      <li><strong>Pay only for actual time used.</strong> If you booked 4 hours and the job only takes 3, you pay for 3.</li>
    </ol>

    <h2>Landlords &amp; Lettings Agents</h2>
    <p>
      We work with a number of landlords and lettings agents across the South-East. Typical engagements include:
    </p>
    <ul>
      <li><strong>End-of-tenancy snag lists</strong> — running through the inventory check and fixing every small issue before the next tenant arrives.</li>
      <li><strong>Mid-tenancy minor repairs</strong> — door latches, broken blinds, missing silicone, loose handles.</li>
      <li><strong>Inventory preparation</strong> — neutralising a property's small defects so the entry inventory is as clean as possible.</li>
      <li><strong>Voids fix-up days</strong> — booking us for half a day or a full day between tenancies to clear a long list in one visit.</li>
    </ul>
    <p>
      We invoice the landlord or agent directly, can attend with appropriate tenant notice, and provide written job summaries suitable for
      your management records.
    </p>

    <h2>Pricing</h2>
    <p>
      Handyman work is priced one of two ways: an hourly rate (with a one-hour minimum and 15-minute increments after that), or a fixed quote
      for larger pre-defined jobs. Both options include our travel, tools, fixings and consumables. Any materials you ask us to source are
      itemised on the invoice with no hidden markup. For longer engagements — half-day, full-day, or repeat landlord visits — we offer a
      discounted day rate.
    </p>

    <h2>Areas Covered</h2>
    <p>
      Our handyman service runs across <strong>Kent</strong> (Rochester, Gillingham, Chatham, Maidstone, Dartford, Gravesend, Sittingbourne),
      <strong> Greater London</strong> (all 32 boroughs) and <strong>Essex</strong> (Romford, Brentwood, Basildon, Chelmsford, Southend).
      We will gladly travel further afield for larger bookings or repeat work.
    </p>

    <h2>Ready To Hand Us The List?</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a>, send us a WhatsApp using the green button at the bottom of the
      page, or use the form below — we typically reply the same day. For ongoing property care see our{' '}
      <a href="/services/property-maintenance">property maintenance services</a>.
    </p>
  </ServicePageLayout>
);

export default HandymanPage;
